import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, alpha } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  Grid,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
  Typography,
} from "@material-ui/core";
import AutorenewIcon from "@material-ui/icons/Autorenew";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import NoQueueWarning from "../NoQueueWarning";
import MessageVariablesPicker from "../MessageVariablesPicker";
import LogoLoader from "../LogoLoader";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  // Nova classe para scroll personalizado
  customScrollBar: {
    maxHeight: '75vh',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    "&::-webkit-scrollbar-button": {
      display: "none",
      color: '#f3f3f3'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f3f3f3',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#f3f3f3',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#555',
    },
    // Para Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: theme.mode !== 'dark' ? '#888 #f3f3f3' : '#888 #333',    
  },
  dialogTitle: { paddingBottom: 0, "& h2": { fontWeight: 600 } },
  titleSub: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 2 },
  tabs: {
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minWidth: 0, padding: theme.spacing(1, 1.5) },
  },
  tabPanel: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5), minHeight: 360 },
  hidden: { display: "none" },
  helper: { fontSize: 12, color: theme.palette.text.secondary },
  sectionLabel: { fontSize: 13, fontWeight: 600, marginBottom: theme.spacing(0.75) },
  switchCard: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
  },
  cardTitle: { fontSize: 14, fontWeight: 600 },
  cardDesc: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  choiceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  choice: {
    textAlign: "left",
    font: "inherit",
    color: "inherit",
    cursor: "pointer",
    padding: theme.spacing(1.5, 1.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    background: "transparent",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  choiceActive: {
    border: `2px solid ${theme.palette.primary.main}`,
    padding: `calc(${theme.spacing(1.5)}px - 1px) calc(${theme.spacing(1.75)}px - 1px)`,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  messageBlock: {
    padding: theme.spacing(1.5, 1.75, 0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
  },
  actions: { padding: theme.spacing(1.5, 3), gap: theme.spacing(1) },
  actionBtn: { textTransform: "none", fontWeight: 600, borderRadius: 8, boxShadow: "none" },
}));

// Em qual aba fica cada campo validado: ao salvar com erro, a tela pula
// para a aba do problema em vez de falhar sem mostrar onde.
const FIELD_TAB = {
  name: 0,
  greetingMessage: 1,
  timeSendQueue: 2,
  maxUseBotQueues: 2,
  timeUseBotQueues: 2,
  expiresTicket: 2,
};

const TABS = ["Geral", "Mensagens", "Encaminhamento", "Avançado"];

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  timeSendQueue: Yup.number("Must be a number")
    .typeError("Campo obrigatório")
    .integer("Must be an integer")
    .min(0, "Must be greater than or equal to 0")
    .required("Campo obrigatório"),
  maxUseBotQueues: Yup.number("Must be a number")
    .typeError("Campo obrigatório")
    .integer("Must be an integer")
    .min(0, "Must be greater than or equal to 0")
    .required("Campo obrigatório"),
  timeUseBotQueues: Yup.number("Must be a number")
    .typeError("Campo obrigatório")
    .integer("Must be an integer")
    .min(0, "Must be greater than or equal to 0")
    .required("Campo obrigatório"),
  expiresTicket: Yup.number("Must be a number")
    .typeError("Campo obrigatório")
    .integer("Must be an integer")
    .min(0, "Must be greater than or equal to 0")
    .required("Campo obrigatório"),
});

const generateToken = () =>
  Math.random().toString(36).substring(2, 10) +
  Math.random().toString(36).substring(2, 10);

// Saudação padrão de conexões novas. O sistema já monta o menu numerado com
// as filas logo abaixo dela, então o texto é só o cumprimento + o convite.
const DEFAULT_GREETING_MESSAGE =
  "Olá! 👋 Seja bem-vindo(a) ao nosso atendimento.\nPor favor, selecione a opção desejada:";

const WhatsAppModal = ({ open, onClose, whatsAppId, onCreated }) => {
  const classes = useStyles();
  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    token: "",
    provider: "beta",
    timeSendQueue: 0,
    sendIdQueue: 0,
    expiresInactiveMessage: "",
    expiresTicket: 0,
    timeUseBotQueues: 0,
    maxUseBotQueues: 3
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [tab, setTab] = useState(0);
  // "queues" = o cliente cai numa fila; "ai" = um assistente de IA responde.
  const [routeMode, setRouteMode] = useState("queues");
  const lastQueueIdsRef = useRef([]);

  useEffect(() => {
    if (open) setTab(0);
  }, [open, whatsAppId]);

  const greetingMsgRef = useRef();
  const complationMsgRef = useRef();
  const outOfHoursMsgRef = useRef();
  const ratingMsgRef = useRef();
  const activeFieldRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      setLoadingData(true);
      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);
        setWhatsApp({...data, sendIdQueue: data.transferQueueId ?? 0, timeSendQueue: data.timeToTransfer ?? 0});
        data.promptId ? setSelectedPrompt(data.promptId) : setSelectedPrompt(null);
        setRouteMode(data.promptId ? "ai" : "queues");

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Conexão nova já nasce vinculada a todas as filas — esquecer esse vínculo
  // era o principal motivo de "conectei e não chega nada". Com mais de uma
  // fila a saudação é obrigatória, então ela também já vem preenchida.
  useEffect(() => {
    if (!open || whatsAppId) return;
    setSelectedQueueIds(queues.map((q) => q.id));
    setSelectedPrompt(null);
    setRouteMode("queues");
    setWhatsApp({
      ...initialState,
      greetingMessage: queues.length > 1 ? DEFAULT_GREETING_MESSAGE : "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, whatsAppId, queues]);

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = {
      ...values,
      transferQueueId: values.sendIdQueue,
      timeToTransfer: values.timeSendQueue, 
      queueIds: selectedQueueIds,
      promptId: selectedPrompt ? selectedPrompt : null
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];

    try {
      let created = null;
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        const { data } = await api.post("/whatsapp", whatsappData);
        created = data;
      }
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
      if (created && onCreated) onCreated(created);
    } catch (err) {
      // Com mais de uma fila a saudação é obrigatória: leva para a aba dela.
      const code = err?.response?.data?.error || err?.response?.data?.message;
      if (code === "ERR_WAPP_GREETING_REQUIRED") setTab(1);
      toastError(err);
    }
  };

  // Trocar de modo limpa o outro lado (a conexão usa filas OU IA), mas
  // guarda as filas para devolvê-las se o usuário voltar atrás.
  const handleRouteMode = (mode) => {
    if (mode === routeMode) return;
    if (mode === "ai") {
      lastQueueIdsRef.current = selectedQueueIds || [];
      setSelectedQueueIds([]);
    } else {
      setSelectedPrompt(null);
      setSelectedQueueIds(lastQueueIdsRef.current || []);
    }
    setRouteMode(mode);
  };

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
    setSelectedQueueIds([]);
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
  };

  const handleClickMsgVar = async (msgVar, setFieldValue, fieldName) => {
    const el = activeFieldRef.current;
    if (!el) return;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;
    setFieldValue(fieldName || el.name, `${firstHalfText}${msgVar}${secondHalfText}`);
    await new Promise(r => setTimeout(r, 100));
    el.setSelectionRange(newCursorPos, newCursorPos);
    el.focus();
  };

  const panel = (index) => `${classes.tabPanel} ${tab === index ? "" : classes.hidden}`;

  const goToErrorTab = (errors) => {
    const tabs = Object.keys(errors || {})
      .map((field) => FIELD_TAB[field])
      .filter((t) => t !== undefined);
    if (tabs.length) setTab(Math.min(...tabs));
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        {loadingData && <LogoLoader overlay />}
        <DialogTitle className={classes.dialogTitle}>
          {whatsAppId ? "Editar conexão" : "Nova conexão"}
          {whatsAppId && whatsApp.name && (
            <Typography className={classes.titleSub}>{whatsApp.name}</Typography>
          )}
        </DialogTitle>
        <Tabs
          value={tab}
          onChange={(e, value) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          className={classes.tabs}
        >
          {TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={async (values) => {
            // Aguarda a requisição terminar: enquanto isso o Formik mantém
            // isSubmitting=true e o botão desabilitado, evitando envio duplicado.
            await handleSaveWhatsApp(values);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue, validateForm }) => (
            <Form>
              <DialogContent className={classes.customScrollBar}>
                {/* ── Geral ── */}
                <div className={panel(0)}>
                  <Field
                    as={TextField}
                    label="Nome da conexão"
                    autoFocus
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={(touched.name && errors.name) || "Só para identificar aqui dentro. O cliente não vê."}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />

                  <div className={classes.switchCard}>
                    <div>
                      <div className={classes.cardTitle}>Conexão padrão</div>
                      <div className={classes.cardDesc}>
                        Usada quando você inicia uma conversa nova e não escolhe o número.
                      </div>
                    </div>
                    <Field
                      as={Switch}
                      color="primary"
                      name="isDefault"
                      checked={values.isDefault}
                      inputProps={{ "aria-label": "Conexão padrão" }}
                    />
                  </div>

                  <div>
                    <div className={classes.sectionLabel}>Quem atende as mensagens que chegam</div>
                    <div className={classes.choiceGrid}>
                      <button
                        type="button"
                        className={`${classes.choice} ${routeMode === "queues" ? classes.choiceActive : ""}`}
                        aria-pressed={routeMode === "queues"}
                        onClick={() => handleRouteMode("queues")}
                      >
                        <div className={classes.cardTitle}>Filas (setores)</div>
                        <div className={classes.cardDesc}>
                          O cliente escolhe o setor ou cai direto na única fila.
                        </div>
                      </button>
                      <button
                        type="button"
                        className={`${classes.choice} ${routeMode === "ai" ? classes.choiceActive : ""}`}
                        aria-pressed={routeMode === "ai"}
                        onClick={() => handleRouteMode("ai")}
                      >
                        <div className={classes.cardTitle}>Assistente de IA</div>
                        <div className={classes.cardDesc}>
                          A IA responde primeiro e transfere quando precisar.
                        </div>
                      </button>
                    </div>
                  </div>

                  {routeMode === "queues" ? (
                    <div>
                      <QueueSelect
                        selectedQueueIds={selectedQueueIds}
                        onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                      />
                      <div className={classes.helper}>
                        Com uma fila só, o cliente vai direto para ela sem ver menu.
                        {selectedQueueIds?.length > 1 && " Com mais de uma, a saudação (aba Mensagens) é obrigatória."}
                      </div>
                    </div>
                  ) : (
                    <FormControl margin="dense" variant="outlined" fullWidth>
                      <InputLabel>Assistente de IA</InputLabel>
                      <Select
                        labelId="dialog-select-prompt-label"
                        id="dialog-select-prompt"
                        name="promptId"
                        value={selectedPrompt || ""}
                        onChange={handleChangePrompt}
                        label="Assistente de IA"
                        fullWidth
                        MenuProps={{
                          anchorOrigin: { vertical: "bottom", horizontal: "left" },
                          transformOrigin: { vertical: "top", horizontal: "left" },
                          getContentAnchorEl: null,
                        }}
                      >
                        {prompts.map((prompt) => (
                          <MenuItem key={prompt.id} value={prompt.id}>
                            {prompt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {!selectedQueueIds?.length && !selectedPrompt && (
                    <NoQueueWarning type="connection" />
                  )}
                </div>

                {/* ── Mensagens ── */}
                <div className={panel(1)}>
                  <div className={classes.helper}>
                    Mensagens enviadas automaticamente por esta conexão. Deixe em branco para não enviar.
                  </div>
                  {[
                    { name: "greetingMessage", ref: greetingMsgRef, title: "Saudação", desc: "Quando o cliente manda a primeira mensagem. O menu de filas aparece logo abaixo dela." },
                    { name: "complationMessage", ref: complationMsgRef, title: "Encerramento", desc: "Ao finalizar o atendimento." },
                    { name: "outOfHoursMessage", ref: outOfHoursMsgRef, title: "Fora do horário", desc: "Quando chega mensagem fora do expediente." },
                    { name: "ratingMessage", ref: ratingMsgRef, title: "Avaliação", desc: "Pergunta de nota depois do encerramento." },
                  ].map((msg) => (
                    <div key={msg.name} className={classes.messageBlock}>
                      <div className={classes.cardTitle}>{msg.title}</div>
                      <div className={classes.cardDesc}>{msg.desc}</div>
                      <Field
                        as={TextField}
                        multiline
                        minRows={3}
                        fullWidth
                        name={msg.name}
                        inputRef={msg.ref}
                        inputProps={{ "aria-label": msg.title }}
                        onFocus={() => { activeFieldRef.current = msg.ref.current; }}
                        error={touched[msg.name] && Boolean(errors[msg.name])}
                        helperText={touched[msg.name] && errors[msg.name]}
                        variant="outlined"
                        margin="dense"
                      />
                    </div>
                  ))}
                  <div>
                    <div className={classes.helper}>Clique num campo acima e escolha o que inserir:</div>
                    <MessageVariablesPicker
                      disabled={isSubmitting}
                      onClick={value => handleClickMsgVar(value, setFieldValue)}
                    />
                  </div>
                </div>

                {/* ── Encaminhamento ── */}
                <div className={panel(2)}>
                  <div>
                    <div className={classes.cardTitle}>Redirecionar quem ficou sem fila</div>
                    <div className={classes.cardDesc}>
                      Contatos que não escolheram setor vão para a fila abaixo depois do tempo definido.
                    </div>
                  </div>
                  <Grid spacing={2} container>
                    <Grid xs={12} sm={6} item>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel id="sendIdQueue-selection-label">Fila de destino</InputLabel>
                        <Field
                          as={Select}
                          name="sendIdQueue"
                          id="sendIdQueue"
                          label="Fila de destino"
                          labelId="sendIdQueue-selection-label"
                        >
                          <MenuItem value={0}>Não redirecionar</MenuItem>
                          {queues.map(queue => (
                            <MenuItem key={queue.id} value={queue.id}>
                              {queue.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} item>
                      <Field
                        as={TextField}
                        label="Depois de (minutos)"
                        fullWidth
                        name="timeSendQueue"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        error={touched.timeSendQueue && Boolean(errors.timeSendQueue)}
                        helperText={touched.timeSendQueue && errors.timeSendQueue}
                      />
                    </Grid>
                  </Grid>

                  <div>
                    <div className={classes.cardTitle}>Menu de filas (bot)</div>
                    <div className={classes.cardDesc}>
                      Quantas vezes o menu é reenviado se o cliente não escolher, e o intervalo entre envios.
                    </div>
                  </div>
                  <Grid spacing={2} container>
                    <Grid xs={12} sm={6} item>
                      <Field
                        as={TextField}
                        label="Enviar o menu até (vezes)"
                        fullWidth
                        name="maxUseBotQueues"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        error={touched.maxUseBotQueues && Boolean(errors.maxUseBotQueues)}
                        helperText={touched.maxUseBotQueues && errors.maxUseBotQueues}
                      />
                    </Grid>
                    <Grid xs={12} sm={6} item>
                      <Field
                        as={TextField}
                        label="Intervalo entre envios (minutos)"
                        fullWidth
                        name="timeUseBotQueues"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        error={touched.timeUseBotQueues && Boolean(errors.timeUseBotQueues)}
                        helperText={touched.timeUseBotQueues && errors.timeUseBotQueues}
                      />
                    </Grid>
                  </Grid>

                  <div>
                    <div className={classes.cardTitle}>Encerrar por inatividade</div>
                    <div className={classes.cardDesc}>
                      Fecha atendimentos abertos sem resposta depois do tempo definido. Use 0 para nunca encerrar.
                    </div>
                  </div>
                  <Field
                    as={TextField}
                    label="Encerrar após (minutos)"
                    fullWidth
                    name="expiresTicket"
                    variant="outlined"
                    margin="dense"
                    type="number"
                    InputProps={{ inputProps: { min: 0 } }}
                    error={touched.expiresTicket && Boolean(errors.expiresTicket)}
                    helperText={touched.expiresTicket && errors.expiresTicket}
                  />
                  <Field
                    as={TextField}
                    label="Mensagem ao encerrar por inatividade"
                    multiline
                    minRows={3}
                    fullWidth
                    name="expiresInactiveMessage"
                    error={touched.expiresInactiveMessage && Boolean(errors.expiresInactiveMessage)}
                    helperText={touched.expiresInactiveMessage && errors.expiresInactiveMessage}
                    variant="outlined"
                    margin="dense"
                  />
                </div>

                {/* ── Avançado ── */}
                <div className={panel(3)}>
                  <Field
                    as={TextField}
                    label="Token da API"
                    fullWidth
                    name="token"
                    variant="outlined"
                    margin="dense"
                    helperText="Usado para autenticar chamadas à API de envio de mensagens. Deixe em branco se não for usar a API."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Gerar token aleatório">
                            <IconButton
                              size="small"
                              aria-label="Gerar token aleatório"
                              onClick={() => setFieldValue("token", generateToken())}
                            >
                              <AutorenewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </DialogContent>
              <DialogActions className={classes.actions}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  variant="outlined"
                  className={classes.actionBtn}
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={`${classes.btnWrapper} ${classes.actionBtn}`}
                  onClick={() => validateForm().then(goToErrorTab)}
                >
                  {whatsAppId ? "Salvar" : "Criar e conectar"}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
